import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useSessionCtx } from "../context/SessionContext";
import { StreamingVendorControls } from "../pages/DashboardPage/StreamingVendorControls.jsx";
import { pubSub } from "../ably";
import { ThemeToggle } from "../components/ThemeToggle.jsx";

// "2026-09-18 12:20:00" -> "Sep 18, 2026 12:20 PM" (or null if missing/invalid).
function formatDateTime(value) {
  if (value === undefined || value === null || value === "") return null;
  const d = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return String(value);
  const date = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const time = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${date} ${time}`;
}

function initialsOf(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return parts.length
    ? (
        (parts[0][0] || "") +
        (parts.length > 1 ? parts[parts.length - 1][0] : "")
      ).toUpperCase()
    : "";
}

// Top section of the single admin page (MainPage):
//   top bar  - brand + streaming controls; hides on scroll-down
//   header   - session title, time range, type (left) and the moderator (right); sticky
export default function AdminLayout({ children }) {
  const ctx = useSessionCtx();
  const detail = ctx?.session?.data?.detail || {};
  const moderator =
    ctx?.userDetails?.data && typeof ctx.userDetails.data === "object"
      ? ctx.userDetails.data
      : {};
  const moderatorLoading = ctx?.isUserDetailsLoading ?? false;
  // Brand mark = cme-common setting.branding.smlogo (also the tab icon, see MainPage).
  // Hidden when missing or when that url fails to load, instead of a broken image.
  const smlogo = ctx?.session?.data?.setting?.branding?.smlogo;
  const [failedLogo, setFailedLogo] = useState(null);
  const showLogo = Boolean(smlogo) && failedLogo !== smlogo;

  const start = formatDateTime(detail.startTime);
  const end = formatDateTime(detail.endTime);
  const timeRange = start ? (end ? `${start} – ${end}` : start) : null;

  const [showNav, setShowNav] = useState(true);
  const lastY = useRef(0);
  // Live socket state, always shown in the header so the admin can tell the difference
  // between "nothing is syncing" and "I clicked the wrong thing": Connected /
  // Reconnecting (first 30s) / Disconnected (still down after 30s).
  const [realtimeConnected, setRealtimeConnected] = useState(
    () => pubSub.isConnected,
  );
  const [disconnectedLong, setDisconnectedLong] = useState(false);
  useEffect(() => {
    const onConn = (connected) => setRealtimeConnected(connected);
    pubSub.onConnectionChange(onConn);
    return () => pubSub.offConnectionChange(onConn);
  }, []);
  useEffect(() => {
    if (realtimeConnected) {
      setDisconnectedLong(false);
      return undefined;
    }
    const timer = setTimeout(() => setDisconnectedLong(true), 30000);
    return () => clearTimeout(timer);
  }, [realtimeConnected]);
  const connectionStatus = realtimeConnected
    ? "connected"
    : disconnectedLong
      ? "disconnected"
      : "reconnecting";

  // Hide the top bar while scrolling down, show it again when scrolling up.
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      const y = window.scrollY;
      if (y > 100) {
        if (y > lastY.current) setShowNav(false);
        else setShowNav(true);
      } else {
        setShowNav(true);
      }
      lastY.current = y;
    };
    const handler = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          onScroll();
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <main className="lg:px-5 min-h-screen">
        <nav
          className={`bg-white px-3 lg:px-6 flex items-center justify-between h-14 sm:h-16 gap-2 lg:gap-4 lg:rounded-t-2xl sticky top-0 z-10 transition-transform duration-300 ${showNav ? "translate-y-0" : "-translate-y-full"}`}
        >
          <Link
            to="/admin/dashboard"
            className="text-gray-700 no-underline font-semibold text-sm flex items-center gap-2"
          >
            {showLogo && (
              <img
                src={smlogo}
                alt=""
                className="w-8 h-8 rounded-lg object-contain shrink-0"
                onError={() => setFailedLogo(smlogo)}
              />
            )}
            <span className="flex flex-col leading-tight">
              <span>CME Control Panel</span>
              {/* Always visible: Connected / Reconnecting / Disconnected */}
              <span
                className={`inline-flex items-center gap-1 text-[10px] font-semibold ${
                  connectionStatus === "connected"
                    ? "text-emerald-600"
                    : connectionStatus === "disconnected"
                      ? "text-red-600"
                      : "text-amber-600"
                }`}
                title={
                  connectionStatus === "connected"
                    ? "Live updates connected"
                    : "Live updates are down - switches may not sync until it is back"
                }
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                    connectionStatus === "connected"
                      ? "bg-emerald-500"
                      : connectionStatus === "disconnected"
                        ? "bg-red-500"
                        : "bg-amber-500 animate-pulse"
                  }`}
                  aria-hidden="true"
                />
                {connectionStatus === "connected"
                  ? "Connected"
                  : connectionStatus === "disconnected"
                    ? "Disconnected"
                    : "Reconnecting"}
              </span>
            </span>
          </Link>

          <div className="flex items-center gap-2 lg:gap-3 ml-auto">
            <StreamingVendorControls />
            <ThemeToggle />
          </div>
        </nav>

        <header
          className={`sticky z-10 bg-white/80 backdrop-blur-lg border-b border-gray-200 lg:rounded-b-2xl ${showNav ? "top-14 sm:top-16" : "top-0"}`}
        >
          <div className="flex flex-wrap items-center gap-2 px-3 lg:px-6 py-2 bg-white border-b border-gray-200">
            {/* Left: session title + time range, type (the session id is not shown) */}
            <div className="min-w-0 flex-1">
              <h2
                className="text-sm sm:text-base font-semibold text-gray-900 truncate leading-tight"
                title={detail.title || undefined}
              >
                {detail.title || "Untitled session"}
              </h2>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] text-gray-500">
                {timeRange && (
                  <span className="inline-flex items-center gap-1.5">
                    <svg
                      className="w-3 h-3 shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span className="font-medium text-gray-700">
                      {timeRange}
                    </span>
                  </span>
                )}
                {detail.tenet && (
                  <span className="px-1.5 py-1 rounded-full bg-gray-900 text-white text-[10px] font-medium leading-none">
                    {detail.tenet}
                  </span>
                )}
              </div>
            </div>

            {/* Right: moderator (connection status sits under the brand).
                Phones show only the avatar / initials circle; name + label from sm up. */}
            <div
              className="flex items-center gap-2.5 ml-auto min-w-0"
              title={`Moderator: ${moderatorLoading ? "Loading…" : moderator.name || "Not assigned"}`}
            >
              {moderator.image ? (
                <img
                  src={moderator.image}
                  alt={moderator.name || "Moderator"}
                  className="w-8 h-8 rounded-full object-cover shrink-0"
                />
              ) : (
                <span
                  className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-800 to-black text-white text-[11px] font-semibold flex items-center justify-center shrink-0"
                  aria-label={moderator.name ? `Moderator ${moderator.name}` : "Moderator"}
                >
                  {initialsOf(moderator.name) || (
                    <svg
                      className="w-3.5 h-3.5"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M12 12a4 4 0 100-8 4 4 0 000 8zm0 2c-4.42 0-8 2.24-8 5v1h16v-1c0-2.76-3.58-5-8-5z" />
                    </svg>
                  )}
                </span>
              )}
              <div className="hidden sm:block min-w-0 leading-tight">
                <p className="text-xs font-semibold text-gray-900 truncate">
                  {moderatorLoading
                    ? "Loading…"
                    : moderator.name || "Not assigned"}
                </p>
                <p className="text-[10px] text-gray-500">Moderator</p>
              </div>
            </div>
          </div>
        </header>

        {/* No side padding on lg+ (content spans to the page edge gutter); small on phones. */}
        {/* Phones: extra bottom padding so the floating Go Live button never covers content. */}
        <div className="pt-2 pb-24 px-1 sm:py-3 lg:py-4 lg:px-0">{children}</div>
      </main>
    </div>
  );
}
